import { z } from 'zod';
import { Id, IsoDateTime, Phone } from './common.js';
import { EscalationReason } from './conversations.js';
import { LeadType } from './customers.js';

// ─── Universal tools ─────────────────────────────────────────────────────────

export const SearchKnowledgeBaseInput = z.object({
  query: z.string().min(1).max(1000),
  max_results: z.number().int().min(1).max(20).default(5),
});

export const GetBusinessInfoInput = z.object({
  topic: z.enum([
    'hours',
    'location',
    'contact',
    'policies',
    'cancellation_policy',
    'refund_policy',
    'return_policy',
    'delivery',
    'payment_methods',
    'team',
    'languages',
  ]),
});

export const EscalateToMerchantInput = z.object({
  reason: EscalationReason,
  summary: z.string().min(1).max(1000),
});

export const CaptureLeadInput = z.object({
  name: z.string().min(1).max(200),
  phone: z.string().max(40).nullable(),
  email: z.string().email().nullable().optional(),
  lead_type: LeadType.default('interest'),
  interest_details: z.string().max(2000),
  urgency: z.enum(['low', 'medium', 'high']).default('medium'),
  budget_range: z.string().nullable().optional(),
  notes: z.string().default(''),
});

export const UpdateCustomerProfileInput = z.object({
  field: z.enum(['name', 'email', 'address']),
  value: z.string().min(1).max(500),
});

// ─── E-commerce tools ────────────────────────────────────────────────────────

export const SearchProductsInput = z.object({
  query: z.string().nullable().optional(),
  image_ref: z.string().nullable().optional(),
  max_price_cents: z.number().int().nonnegative().nullable().optional(),
  in_stock_only: z.boolean().default(true),
  category: z.string().nullable().optional(),
  max_results: z.number().int().min(1).max(10).default(5),
});

export const GetProductDetailsInput = z.object({
  product_id: Id,
});

export const CheckInventoryInput = z.object({
  product_id: Id,
  variant_id: Id.nullable().optional(),
});

export const CreateCodOrderInput = z.object({
  customer_name: z.string().min(1).max(200),
  customer_phone: Phone,
  delivery_address: z.string().min(1).max(1000),
  // محافظة / governorate (or alias). Server resolves the delivery fee from
  // business_policies.delivery_zones. If omitted, the server tries to infer
  // it from `delivery_address`; if no zone matches, the order is refused.
  governorate: z.string().min(1).max(120).nullable().optional(),
  items: z
    .array(
      z.object({
        product_id: Id,
        variant_id: Id.nullable().optional(),
        quantity: z.number().int().positive(),
      }),
    )
    .min(1),
  notes: z.string().max(2000).default(''),
});

export const SendStoreLinkInput = z.object({
  message_text: z.string().max(1000),
});

export const GetOrderStatusInput = z.object({
  order_id: Id,
  customer_phone: Phone,
});

export const GetCustomerOrdersInput = z.object({
  customer_phone: Phone,
  limit: z.number().int().min(1).max(20).default(5),
});

export const ApplyPromoCodeInput = z.object({
  order_id: Id,
  code: z.string().max(60),
});

// ─── Service business tools ──────────────────────────────────────────────────

export const CheckAvailabilityInput = z.object({
  service_id: Id,
  team_member_id: Id.nullable().optional(),
  start_range: IsoDateTime,
  end_range: IsoDateTime,
  max_slots: z.number().int().min(1).max(20).default(6),
});

export const BookAppointmentInput = z.object({
  customer_name: z.string().min(1).max(200),
  customer_phone: Phone,
  service_id: Id,
  team_member_id: Id.nullable().optional(),
  start_at: IsoDateTime,
  customer_notes: z.string().max(2000).default(''),
});

export const CancelAppointmentInput = z.object({
  appointment_id: Id,
  reason: z.string().max(500).default(''),
});

export const RescheduleAppointmentInput = z.object({
  appointment_id: Id,
  new_start_at: IsoDateTime,
});

export const GetCustomerAppointmentsInput = z.object({
  customer_phone: Phone,
  status: z
    .enum(['pending', 'confirmed', 'cancelled', 'completed', 'no_show', 'all'])
    .default('all'),
});

// ─── SaaS / support ──────────────────────────────────────────────────────────

export const CreateSupportTicketInput = z.object({
  customer_name: z.string(),
  customer_contact: z.string(),
  issue_description: z.string().min(1).max(4000),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  category: z.string().max(120),
});

// ─── Phase 2: Meta-native + escalation tools ────────────────────────────────

// Hand off to a human via WhatsApp deep link. Replaces `escalate_to_merchant`
// for IG/FB DM conversations — the AI generates a wa.me link the customer
// taps to continue with a human on WhatsApp Business.
export const EscalateToHumanInput = z.object({
  reason: z.enum([
    'return_or_refund',
    'damaged_product',
    'custom_order',
    'complaint',
    'wholesale_inquiry',
    'customer_requested_human',
    'other',
  ]),
  // Short context line the merchant sees on WhatsApp (prefilled in the link).
  summary: z.string().min(1).max(500),
});

// Fetch context about the social post this DM conversation originated from.
// Used when a customer DMs us after being auto-DM'd from a comment — the AI
// calls this to ground its first reply in the exact product they commented on.
export const FetchPostContextInput = z.object({
  // Optional: limit to the most recent N comment events to scan. Default 5.
  recent_n: z.number().int().min(1).max(20).default(5),
});

// ─── Tool definitions ────────────────────────────────────────────────────────

export const ToolName = z.enum([
  'search_knowledge_base',
  'get_business_info',
  'escalate_to_merchant',
  'escalate_to_human',
  'capture_lead',
  'update_customer_profile',
  'search_products',
  'get_product_details',
  'check_inventory',
  'create_cod_order',
  'send_store_link',
  'get_order_status',
  'get_customer_orders',
  'apply_promo_code',
  'check_availability',
  'book_appointment',
  'cancel_appointment',
  'reschedule_appointment',
  'get_customer_appointments',
  'create_support_ticket',
  'fetch_post_context',
]);
export type ToolName = z.infer<typeof ToolName>;

export const TOOL_INPUTS = {
  search_knowledge_base: SearchKnowledgeBaseInput,
  get_business_info: GetBusinessInfoInput,
  escalate_to_merchant: EscalateToMerchantInput,
  escalate_to_human: EscalateToHumanInput,
  capture_lead: CaptureLeadInput,
  update_customer_profile: UpdateCustomerProfileInput,
  search_products: SearchProductsInput,
  get_product_details: GetProductDetailsInput,
  check_inventory: CheckInventoryInput,
  create_cod_order: CreateCodOrderInput,
  send_store_link: SendStoreLinkInput,
  get_order_status: GetOrderStatusInput,
  get_customer_orders: GetCustomerOrdersInput,
  apply_promo_code: ApplyPromoCodeInput,
  check_availability: CheckAvailabilityInput,
  book_appointment: BookAppointmentInput,
  cancel_appointment: CancelAppointmentInput,
  reschedule_appointment: RescheduleAppointmentInput,
  get_customer_appointments: GetCustomerAppointmentsInput,
  create_support_ticket: CreateSupportTicketInput,
  fetch_post_context: FetchPostContextInput,
} as const;

export type ToolInput<N extends ToolName> = z.infer<(typeof TOOL_INPUTS)[N]>;
